import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "@/lib/data";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", open);
    return () => document.body.classList.remove("no-scroll");
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src="/logo-full-sm.png"
            alt="SKVY Harmony Placement"
            className="h-12 sm:h-14 w-auto object-contain shrink-0"
            style={{ filter: "brightness(1.6) contrast(1.15) drop-shadow(0 4px 12px rgba(59,130,246,0.4))" }}
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-poppins font-semibold text-white text-[15px] tracking-tight whitespace-nowrap">SKVY Harmony Placement</span>
            <span className="font-inter text-[10px] text-[#10B981] tracking-[0.18em] uppercase mt-1">US IT Staffing</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-3.5 py-2 rounded-full text-[13.5px] font-inter transition-colors ${
                  isActive ? "text-white" : "text-white/60 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-pill"
                      className="absolute inset-0 -z-10 rounded-full bg-white/5 border border-white/10"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-hire"
            className="text-[13px] font-inter font-medium text-white px-5 py-2.5 rounded-full transition-colors"
            style={{ background: "linear-gradient(120deg, #1E3A8A, #10B981)", boxShadow: "0 10px 30px -12px rgba(16,185,129,0.5)" }}
          >
            Upload Resume
          </Link>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden glass border-t border-white/5"
            data-testid="nav-mobile-panel"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl font-inter text-[15px] ${
                      isActive ? "bg-white/5 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-cta"
                className="mt-3 text-center text-white font-medium px-5 py-3 rounded-full"
                style={{ background: "linear-gradient(120deg, #1E3A8A, #10B981)" }}
              >
                Upload Resume
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
